// src/signalr/SignalRContext.tsx
import { createContext, useEffect, useState, useCallback, useRef } from "react";
import type { ReactNode } from "react";
import { HubConnection } from "@microsoft/signalr";
import {
  getSignalRConnection,
  resetSignalRConnection,
} from "../signalR/signalRConnection";
import {
  getCustomerAlertsConnection,
  resetCustomerAlertsConnection,
} from "../signalR/customerAlertsConnection";
import { useAppSelector } from "@/store/hooks";
import { selectIsCustomer } from "@/store/authSlice";

interface SignalRContextValue {
  connection: HubConnection | null;
  isConnected: boolean;
  sendMessage: (method: string, ...args: unknown[]) => Promise<void>;
}

export const SignalRContext = createContext<SignalRContextValue>({
  connection: null,
  isConnected: false,
  sendMessage: async () => {},
});

export const SignalRProvider = ({ children }: { children: ReactNode }) => {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const isCustomer = useAppSelector(selectIsCustomer);
  const activeConnectionRef = useRef<HubConnection | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let isDisposed = false;

    const clearRetryTimer = () => {
      if (!retryTimerRef.current) return;
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    };

    const stopConnection = async (conn: HubConnection | null) => {
      if (!conn || conn.state === "Disconnected") return;
      try {
        await conn.stop();
      } catch (err) {
        console.warn("SignalR stop failed", err);
      }
    };

    const startConnection = async () => {
      clearRetryTimer();

      // Role changes require a brand-new connection so server-side group assignment is
      // recalculated for the current role/customer scope.
      await stopConnection(activeConnectionRef.current);
      activeConnectionRef.current = null;
      setConnection(null);
      setIsConnected(false);

      resetSignalRConnection();
      resetCustomerAlertsConnection();

      if (isDisposed) return;

      // Customer users connect to Azure SignalR (serverless) so alerts reach them across
      // all API instances. Internal users use the in-memory hub.
      const conn = isCustomer
        ? await getCustomerAlertsConnection()
        : getSignalRConnection();
      activeConnectionRef.current = conn;

      if (isDisposed) {
        await stopConnection(conn);
        return;
      }

      if (
        conn.state === "Connected" ||
        conn.state === "Connecting" ||
        conn.state === "Reconnecting"
      ) {
        setConnection(conn);
        setIsConnected(conn.state === "Connected");
        return;
      }

      conn.onreconnected(() => {
        if (isDisposed) return;
        setIsConnected(true);
      });

      conn.onclose(() => {
        if (isDisposed) return;
        console.warn("SignalR disconnected");
        setIsConnected(false);
      });

      try {
        await conn.start();
        if (isDisposed) return;
        setConnection(conn);
        setIsConnected(true);
      } catch (err) {
        if (isDisposed) return;
        console.error("SignalR start failed, retrying in 5s", err);
        setIsConnected(false);
        retryTimerRef.current = setTimeout(() => {
          void startConnection();
        }, 5000);
      }
    };

    void startConnection();

    return () => {
      isDisposed = true;
      clearRetryTimer();
      const conn = activeConnectionRef.current;
      activeConnectionRef.current = null;
      setConnection(null);
      setIsConnected(false);
      void stopConnection(conn);
    };
  }, [isCustomer]);

  const sendMessage = useCallback(
    async (method: string, ...args: unknown[]) => {
      if (connection && isConnected) {
        try {
          await connection.invoke(method, ...args);
        } catch (err) {
          console.error(`SignalR send '${method}' failed`, err);
        }
      } else {
        console.warn("SignalR not connected, message skipped");
      }
    },
    [connection, isConnected],
  );

  return (
    <SignalRContext.Provider value={{ connection, isConnected, sendMessage }}>
      {children}
    </SignalRContext.Provider>
  );
};
